import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useState
} from "react";

import { useDispatch, useSelector } from "react-redux";
import { useI18n } from "../../hooks/useI18n";
import {
  Checkbox,
  FormControl,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectProps
} from "@material-ui/core";
import { AppState } from "../../store";
import { Category, CategoryId } from "../../models/Product";
import { Language } from "../../models/Translation";
import { thunkFetchProductCategories } from "../../store/products/action";
import { thunkFetchCurrentTenantLanguages } from "../../store/tenants/actions";
import { useLoadIfEmpty } from "../../hooks/useLoadIfEmpty";

interface CategoryMultiSelectProps extends Omit<SelectProps, "loading"> {
  label?: string;
  onSelected?: (value: ReadonlyArray<Category>) => void;
  variant?: "filled" | "outlined" | "standard";
  value?: ReadonlyArray<CategoryId>;
}

export const CategoryMultiSelect: FunctionComponent<
  CategoryMultiSelectProps
> = ({
  label,
  onSelected,
  onChange,
  variant = "standard",
  value = [],
  ...props
}) => {
  const { t } = useI18n();
  const dispatch = useDispatch();
  const defaultLabel = label || t("common.formFields.category");
  const tenantLanguages =
    useSelector<AppState, ReadonlyArray<Language>>(
      s => s.currentTenant.languages
    ) || [];

  const categories =
    useSelector<AppState, ReadonlyArray<Category>>(
      s => s.storeClusterProducts.categories
    ) || [];
  const [state, setState] = useState<ReadonlyArray<Category>>([]);

  useEffect(() => {
    if (!categories.length) {
      dispatch(thunkFetchProductCategories());
    }
  }, []);

  useLoadIfEmpty(!tenantLanguages.length, thunkFetchCurrentTenantLanguages);

  const handleChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    child: React.ReactNode
  ) => {
    if (onSelected) {
      let ids = event.target.value as Array<CategoryId>;
      let values = state.filter(x => ids.includes(x.id));

      onSelected(values ?? []);
    }
    if (onChange) {
      onChange(event, child);
    }
  };

  const renderNames = (ids: ReadonlyArray<CategoryId>): ReactNode => {
    return state
      .filter(x => ids.includes(x.id))
      .map(x => x.translation.values[tenantLanguages[0].code])
      .join(", ");
  };

  useEffect(() => {
    let filtered = categories.length > 0 && categories;

    if (filtered) {
      setState(
        [...filtered].sort((a: any, b: any) => {
          if (
            a?.translation?.values == undefined ||
            b?.translation?.values == undefined
          ) {
            return a;
          }
          const aa = a?.translation?.values[tenantLanguages[0].code];
          if (aa !== undefined) {
            return aa.localeCompare(
              b.translation.values[tenantLanguages[0].code]
            );
          }
        })
      );
    }
  }, [categories]);

  return (
    <FormControl
      style={props.style}
      variant={variant}
      className={props.className}
    >
      <InputLabel
        id="category-multi-select-label"
        style={
          variant === "outlined"
            ? {
                backgroundColor: "#fff",
                paddingLeft: "0.5rem",
                paddingRight: "0.5rem"
              }
            : undefined
        }
      >
        {defaultLabel}
      </InputLabel>
      <Select
        multiple
        value={value}
        onChange={handleChange}
        labelId="category-multi-select-label"
        id="category-multi-select"
        autoWidth={true}
        input={<Input />}
        disabled={props.disabled}
        renderValue={value => renderNames(value as string[])}
      >
        {state &&
          state.length > 0 &&
          state.map(x => (
            <MenuItem key={x.id} value={x.id}>
              <Checkbox checked={value.indexOf(x.id) > -1} />
              <ListItemText
                primary={x?.translation?.values[tenantLanguages[0].code]}
              />
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};
